/**
 * tableMigration
 *
 * Lazy migration from the legacy `smartTable` custom node (which stored data in
 * `attrs.initialData: { headers, body }`) to native TipTap/ProseMirror table
 * nodes (`table` > `tableRow` > `tableHeader`/`tableCell` > `paragraph`).
 *
 * Reason (CONTRATO v3, cambio 4): there is now ONE canonical in-editor table
 * representation — native table nodes. `smartTable` is removed. This runs on
 * document JSON BEFORE it enters the editor, so no `smartTable` node ever
 * reaches the (now unregistered) extension. Saving then persists native tables.
 */

/**
 * Build a native tableCell/tableHeader node from a raw cell value.
 *
 * @param {string} type - 'tableHeader' or 'tableCell'
 * @param {*} value - raw cell value (coerced to string)
 * @returns {object} ProseMirror node JSON
 */
const buildCell = (type, value) => {
  const text = value == null ? '' : String(value);
  return {
    type,
    content: [
      {
        type: 'paragraph',
        // Empty paragraphs must have no `content` (ProseMirror rejects empty text nodes).
        content: text ? [{ type: 'text', text }] : [],
      },
    ],
  };
};

/**
 * Convert a single legacy `smartTable` node into a native `table` node.
 *
 * @param {object} node - the smartTable node JSON
 * @returns {object|null} native table node JSON, or null if unmigratable
 */
const smartTableToNative = (node) => {
  const data = node?.attrs?.initialData;
  if (!data || typeof data !== 'object') return null;

  const headers = Array.isArray(data.headers) ? data.headers : null;
  const body = Array.isArray(data.body) ? data.body : [];

  // Determine column count from headers or the widest body row.
  const colCount =
    (headers && headers.length) ||
    body.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);

  if (!colCount) return null;

  const rows = [];

  if (headers && headers.length) {
    rows.push({
      type: 'tableRow',
      content: headers.map((h) => buildCell('tableHeader', h)),
    });
  }

  for (const row of body) {
    const cells = Array.isArray(row) ? row : [];
    // Pad/truncate to colCount so every row is rectangular.
    const normalized = Array.from({ length: colCount }, (_, i) => cells[i]);
    rows.push({
      type: 'tableRow',
      content: normalized.map((c) => buildCell('tableCell', c)),
    });
  }

  if (!rows.length) return null;

  const nativeTable = { type: 'table', content: rows };
  // Preserve alignment attr if present (native Table supports it downstream).
  if (node.attrs && node.attrs.align) {
    nativeTable.attrs = { align: node.attrs.align };
  }
  return nativeTable;
};

/**
 * Recursively walk a node, replacing every `smartTable` descendant with a
 * native `table` node. Malformed smartTable nodes are dropped (with a warning).
 *
 * @param {object} node - ProseMirror node JSON
 * @returns {object|null} the (possibly transformed) node, or null to drop it
 */
const migrateNode = (node) => {
  if (!node || typeof node !== 'object') return node;

  if (node.type === 'smartTable') {
    const native = smartTableToNative(node);
    if (!native) {
      // eslint-disable-next-line no-console
      console.warn('migrateSmartTables: dropping smartTable node with missing/malformed initialData', node?.attrs);
      return null;
    }
    return native;
  }

  if (Array.isArray(node.content)) {
    const migrated = node.content.map(migrateNode).filter(Boolean);
    return { ...node, content: migrated };
  }

  return node;
};

/**
 * Migrate every `smartTable` node in a TipTap document JSON to native tables.
 * Pure function — returns a new object, does not mutate the input.
 *
 * @param {object|string} tiptapJson - document JSON (or a string, returned as-is)
 * @returns {object|string} migrated document JSON
 */
export const migrateSmartTables = (tiptapJson) => {
  if (!tiptapJson || typeof tiptapJson !== 'object') {
    // Strings (raw HTML) and empty values pass through untouched.
    return tiptapJson;
  }
  try {
    return migrateNode(tiptapJson);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('migrateSmartTables: failed, returning original content', error);
    return tiptapJson;
  }
};

export default migrateSmartTables;
